import React, { useState, useEffect } from 'react';
import { CircularProgress, Box, Table, TableHead, TableRow, TableBody, TableCell, Typography } from '@mui/material';
import { MarketplaceChip } from '../../marketplaceChip';
import { useTheme } from '@mui/material/styles';
import { Tenant } from '../../../models/tenant';
import { useTranslation } from 'react-i18next';
import NegotiationForm from '../../../pages/loadSearch/components/NegotiationForm';
import { StandardDialog } from '../base';
import { PotentialOffer, TruckDetails, OfferStatus, timocomPluginData } from '../../../types';
import { apiGet, apiPost } from '../../../services/apiClient';
import { toast } from '../../../utils/toast';
import { Negotiation } from '../../../models/negotiation';
import { OfferRenderer } from '../../../renderers/OfferRenderer';
import { triggerNegotiationAlertsRefresh } from '../../../context/negotiationAlertsContext';

export interface NegotiationParams {
  estimatedPrice: number;
  fairPrice: number;
  minimumAcceptance: number;
  targetPrice: number;
  badSituation: boolean;
  goodSituation: boolean;
  autoRespond: boolean;
  userOwnName: boolean;
  vehicle_auto_nego_session_id: number;
  minAccept: number;
  localNego?: boolean;
  freightExchangeFormat?: boolean;
  autoSendInitial?: boolean;
  useMessenger?: boolean;
}

export interface NegotiationActions {
  handleCloseNegotiation: (offer: PotentialOffer) => Promise<void>;
  handleOpenNegotiationModal: (offer: PotentialOffer) => void;
}

enum SchedulePeriodType {
  PAPERWORK = 0,
  EN_ROUTE = 1,
  ON_BREAK = 2,
}

interface SchedulePeriod {
  driver_id: number;
  startTime: Date;
  endTime: Date;
  type: SchedulePeriodType;
}

export interface Planning {
  overtime: boolean;
  schedule: SchedulePeriod[];
}

interface Prices {
  estimatedPrice: number;
  offerDistance: number;
  offerTolls: boolean;
  // approachPrice/Distance/Tolls are null when no approach source was available at compute time
  // (no vehicle currentPosition and no geocoded end on the prior booking) — distinct from 0 km.
  approachPrice: number | null;
  approachDistance: number | null;
  approachTolls: boolean | null;
  totalPrice: number;
  totalDistance: number;
  offerPreciseMode: boolean;
  approachPreciseMode: boolean;
  ltlFactor: number;
  planning?: Planning;
}

export interface NegotiationDialogProps {
  open: boolean;
  clientPrice?: number;
  initialEstimatedPrice?: number;
  initialFairPrice?: number;
  isStarting?: boolean;
  isEditing?: boolean;
  negotiation?: Negotiation | null;
  offer?: PotentialOffer;
  vehicle: TruckDetails | null;
  tenantData: Tenant | null;
  actions: NegotiationActions;
  onRefresh: () => void;
  setNegotiation: React.Dispatch<React.SetStateAction<Negotiation | null>>;
  setNegotiationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
  onStart: (offerId: number, params: NegotiationParams) => Promise<void>;
  onSendMessage?: (message: string, subject?: string) => void;
  onAccept?: (vehicle: TruckDetails | null, offer: PotentialOffer, emailResponse?: string, includePlates?: boolean) => Promise<void>;
  onDialogClose?: () => void; // Optional callback for dialog close
}

export default function NegotiationDialog({
  open,
  clientPrice,
  initialEstimatedPrice = 0,
  initialFairPrice,
  isStarting = false,
  negotiation,
  offer,
  vehicle,
  tenantData,
  actions,
  onRefresh,
  setNegotiation,
  setNegotiationModalOpen,
  onClose,
  onStart,
  onSendMessage,
  onAccept,
  onDialogClose
}: NegotiationDialogProps): JSX.Element {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<Partial<NegotiationParams> | null>(() => {
    const initHasEmail = !!offer?.contactPerson?.email;
    const initHasMessenger = !!(offer && timocomPluginData(offer)?.useMessenger);
    const initMessengerOnly = !initHasEmail && initHasMessenger;
    const isTimocom = offer?.externalPlatform === 'TimocomMarketplace';
    return {
      estimatedPrice: 0,
      fairPrice: 0,
      minimumAcceptance: 0,
      targetPrice: 0,
      badSituation: false,
      goodSituation: false,
      autoRespond: false,
      userOwnName: false,
      vehicle_auto_nego_session_id: 0,
      minAccept: 0,
      freightExchangeFormat: isTimocom && !initMessengerOnly,
      autoSendInitial: true,
      useMessenger: initMessengerOnly,
    };
  });
  const [planning, setPlanning] = useState<Planning | undefined>(undefined);
  const [loadingPrices, setLoadingPrices] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [errors, setErrors] = useState<{
    estimatedPrice?: string;
    fairPrice?: string;
    minimumAcceptance?: string;
    targetPrice?: string;
    minAccept?: string;
  }>({});
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [includePlates, setIncludePlates] = useState<boolean>(true);

  const theme = useTheme();

  const hasEmail = !!offer?.contactPerson?.email;
  const hasMessenger = !!(offer && timocomPluginData(offer)?.useMessenger);
  const isMessengerOnly = !hasEmail && hasMessenger;

  // Build the plate block string that matches the backend format
  const plateBlock = vehicle?.truckPlate
    ? (vehicle.trailerPlate
      ? `\n\n---\nTruck: ${vehicle.truckPlate}\nTrailer: ${vehicle.trailerPlate}`
      : `\n\n---\nTruck: ${vehicle.truckPlate}`)
    : '';

  // Toggle plate block in the message when checkbox changes (only for acceptance/validation flow)
  const isWaitingFinalConfirm = offer?.status === OfferStatus.NEGOTIATION_WAITING_FINAL_CONFIRM;
  useEffect(() => {
    if (!plateBlock || !isWaitingFinalConfirm) return;
    setMessage(prev => {
      if (includePlates) {
        return prev.includes(plateBlock) ? prev : prev + plateBlock;
      } else {
        return prev.includes(plateBlock) ? prev.replace(plateBlock, '') : prev;
      }
    });
  }, [includePlates]);

  useEffect(() => {
    if (open && initialEstimatedPrice > 0) {
      setPlanning(undefined);
      setIncludePlates(true);
      // Use prices from negotiation if available, otherwise use initial values
      if (negotiation) {
        const actualEstimatedPrice = initialEstimatedPrice;
        const actualFairPrice = negotiation.fairPrice || initialFairPrice || actualEstimatedPrice;
        const actualMaxClientFairPrice = clientPrice && clientPrice > actualFairPrice ? clientPrice : actualFairPrice;
        const actualTargetPrice = negotiation.targetPrice || Number((actualMaxClientFairPrice * (tenantData?.targetPriceMarginFactor || 1)).toFixed(2));
        const actualMinAccept = negotiation.minimumAcceptancePrice || actualMaxClientFairPrice;

        // set the AI created message for human review and confirmation
        const aiBody = negotiation?.emailBodies?.find((body) => ['AI', 'MANAGER'].includes(body.sender) && !body.sent);
        if (aiBody) setMessage(isWaitingFinalConfirm && plateBlock ? aiBody.body + plateBlock : aiBody.body);
        else setMessage('');

        setFormData(prev => ({
          ...prev,
          estimatedPrice: actualEstimatedPrice,
          fairPrice: actualFairPrice,
          targetPrice: actualTargetPrice,
          minAccept: actualMinAccept,
          freightExchangeFormat: !isMessengerOnly && offer?.externalPlatform === 'TimocomMarketplace',
          autoSendInitial: prev?.autoSendInitial ?? true,
          useMessenger: !!negotiation.useMessenger,
        }));
      } else {
        // API call is lengthy, so in the meantime we set initial values no matter what
        const fairPrice = initialFairPrice || initialEstimatedPrice;
        const maxClientFairPrice = clientPrice && clientPrice > fairPrice ? clientPrice : fairPrice;
        setFormData(prev => ({
          ...prev,
          estimatedPrice: initialEstimatedPrice,
          fairPrice: fairPrice,
          targetPrice: Number((maxClientFairPrice * (tenantData?.targetPriceMarginFactor || 1)).toFixed(2)),
          minAccept: maxClientFairPrice,
          freightExchangeFormat: !isMessengerOnly && offer?.externalPlatform === 'TimocomMarketplace',
          autoSendInitial: prev?.autoSendInitial ?? true,
          useMessenger: prev?.useMessenger ?? isMessengerOnly,
        }));
        if (vehicle !== null && offer !== undefined) {
          setLoadingPrices(true);
          apiGet(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${vehicle.id}/offer/${offer.id}/precise_prices`).
            then(res => res.json()).
            then((payload) => {
              const prices: Prices = payload.result;
              // Apply LTL factor: reduce loaded-leg price for partial loads, keep approach as-is
              const ltlFactor = prices.ltlFactor ?? 1;
              const ltlAdjustedPrice = Number((prices.estimatedPrice * ltlFactor + (prices.totalPrice - prices.estimatedPrice)).toFixed(2));
              const maxClientTotalPrice = clientPrice && clientPrice > ltlAdjustedPrice ? clientPrice : ltlAdjustedPrice;
              setFormData(prev => ({
                ...prev,
                estimatedPrice: prices.estimatedPrice,
                fairPrice: ltlAdjustedPrice,
                targetPrice: Number((maxClientTotalPrice * (tenantData?.targetPriceMarginFactor || 1)).toFixed(2)),
                minAccept: maxClientTotalPrice,
              }));
              setPlanning(prices.planning);
              setLoadingPrices(false);
            }).catch((_) => {
              toast.error(t('Could not get precise prices!'));
              setLoadingPrices(false);
            });
        }
      }

      setErrors({});
    }
  }, [open, initialEstimatedPrice, initialFairPrice, tenantData?.targetPriceMarginFactor, negotiation]);

  const validateForm = (): boolean => {
    const newErrors: { estimatedPrice?: string; fairPrice?: string; targetPrice?: string; minAccept?: string } = {};

    if (!formData?.estimatedPrice || formData.estimatedPrice <= 0) {
      newErrors.estimatedPrice = t('Estimated price must be greater than 0');
    }

    if (!formData?.fairPrice || formData.fairPrice <= 0) {
      newErrors.fairPrice = t('Fair price must be greater than 0');
    }

    if (!formData?.targetPrice || formData.targetPrice <= 0) {
      newErrors.targetPrice = t('Target price must be greater than 0');
    }

    if (!formData?.minAccept || formData.minAccept <= 0) {
      newErrors.minAccept = t('Minimum acceptance price must be greater than 0');
    }

    if (formData?.targetPrice && formData?.fairPrice && formData?.targetPrice < formData?.fairPrice) {
      newErrors.targetPrice = t('Target price cannot be lower than fair price');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStart = () => {
    if (offer?.id && validateForm()) {
      onStart(offer.id, {
        estimatedPrice: formData?.estimatedPrice || 0,
        fairPrice: formData?.fairPrice || 0,
        minimumAcceptance: formData?.minimumAcceptance || 0,
        targetPrice: formData?.targetPrice || 0,
        badSituation: formData?.badSituation || false,
        goodSituation: formData?.goodSituation || false,
        autoRespond: formData?.autoRespond || false,
        userOwnName: formData?.userOwnName || false,
        vehicle_auto_nego_session_id: formData?.vehicle_auto_nego_session_id || 0,
        minAccept: formData?.minAccept || 0,
        localNego: formData?.localNego || false,
        freightExchangeFormat: formData?.freightExchangeFormat || false,
        autoSendInitial: formData?.autoSendInitial ?? true,
        useMessenger: formData?.useMessenger ?? false,
      }).then(() => {
        setFormData(null);
      });
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      onSendMessage?.(message);
    }
  };

  const handleDiscardPendingReply = async () => {
    if (negotiation) {
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${negotiation.vehicle_id}/negotiation/${negotiation.id}/discard_pending_reply`);
      setNegotiationModalOpen(false);
      onRefresh();
      triggerNegotiationAlertsRefresh();
    }
  };

  const handleReopenNegotiation = async () => {
    if(negotiation) {
      await apiPost(`${process.env.REACT_APP_API_URL}/api/v1/vehicle/${negotiation.vehicle_id}/negotiation/${negotiation.id}/edit`, { reopen: true });
      setNegotiationModalOpen(false);
      onRefresh();
      triggerNegotiationAlertsRefresh();

    }
  };

  const handleAccept = async () => {
    if (!offer) return;
    try {
      setConfirmLoading(true);
      await onAccept?.(vehicle, offer, message);
      // Close dialog and refresh offers on success
      setNegotiationModalOpen(false);
      onRefresh();
      triggerNegotiationAlertsRefresh();
    } catch (err) {
      // onAccept handles toasts/errors; keep dialog open on failure
      console.error('Failed to accept negotiation:', err);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleClose = (): void => {
    if (!isStarting) {
      onClose();
      setFormData(null);
      if (typeof onDialogClose === 'function') {
        onDialogClose();
      }
    }
  };

  const offerRenderer = offer ? new OfferRenderer(offer, theme, tenantData) : undefined;

  const [arePricesUpdated, setArePricesUpdated] = useState<boolean>(false);

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title=""
      hideHeader
      subtitle=""
      maxWidth={negotiation ? 'lg' : 'md'}
      width={negotiation ? 'lg' : 'md'}
      height={{ xs: '90vh', sm: '80vh', md: '75vh', lg: '85vh' }}
      primaryAction={!negotiation ? {
        label: t('Start'),
        onClick: handleStart,
        loading: isStarting
      } : isWaitingFinalConfirm ? {
        label: t('Accept'),
        onClick: handleAccept,
        loading: confirmLoading,
        disabled: confirmLoading
      } : {
        label: t('Send'),
        onClick: handleSendMessage,
        loading: isStarting,
        disabled: !message.trim() || arePricesUpdated
      }}
      secondaryAction={{
        label: t('Close'),
        onClick: handleClose,
        disabled: isStarting
      }}
      tertiaryAction={
        offer?.status && [
          OfferStatus.NEGOTIATION_MANUALLY_CLOSED_UNDISCLOSED,
          OfferStatus.NEGOTIATION_DECLINED
        ].includes(offer?.status) &&
          negotiation?.emailBodies && negotiation.emailBodies.length > 0 &&
          !negotiation.emailBodies[negotiation.emailBodies.length - 1].sent
          ? {
            label: t('Discard pending reply'),
            onClick: handleDiscardPendingReply,
          }
          : undefined
      }
      quaternaryAction={
        offer?.status === OfferStatus.NEGOTIATION_DECLINED ?
          {
            label: t('Reopen negotiation'),
            onClick: handleReopenNegotiation,
          }
          : undefined
      }
      contentSx={{
        p: { xs: 1.5, sm: 1.5, md: 1.5 },
        pt: { xs: 1, sm: 1.25, md: 1.25 },
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {loadingPrices && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
          <CircularProgress size={32} />
        </Box>
      )}
      {offer && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {`${t('Offer')} #${offer.id}`}
          </Typography>
          <MarketplaceChip externalPlatform={offer.externalPlatform} size="sm" />
        </Box>
      )}
      {offerRenderer && (() => (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell variant="head" align="center">Route</TableCell>
              <TableCell variant="head" align="center">Timeline</TableCell>
              <TableCell variant="head" align="center">Price</TableCell>
              <TableCell variant="head" align="center">Distance</TableCell>
              <TableCell variant="head" align="center">W/L</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{offerRenderer.renderRoute()}</TableCell>
              <TableCell>{offerRenderer.renderTimeline()}</TableCell>
              <TableCell>{offerRenderer.renderPrices()}</TableCell>
              <TableCell>{offerRenderer.renderDistance()}</TableCell>
              <TableCell>{offerRenderer.renderWL()}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ))()}
      <NegotiationForm
        negotiation={negotiation}
        offer={offer}
        vehicle={vehicle}
        formData={formData}
        planning={planning}
        message={message}
        errors={errors}
        targetPriceMarginFactor={tenantData?.targetPriceMarginFactor || 1}
        actions={actions}
        arePricesUpdated={arePricesUpdated}
        setArePricesUpdated={setArePricesUpdated}
        onRefresh={onRefresh}
        setNegotiation={setNegotiation}
        setNegotiationModalOpen={setNegotiationModalOpen}
        setMessage={setMessage}
        setFormData={setFormData}
        includePlates={includePlates}
        setIncludePlates={setIncludePlates}
        hasMessenger={hasMessenger}
        useMessenger={negotiation ? negotiation.useMessenger === true : !!formData?.useMessenger}
      />
    </StandardDialog>
  );
}
